import { useEffect, useState } from 'react';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera } from 'lucide-react';

const CAT_COLORS = { progress: 'bg-[#4a9ac5]/10 text-[#2a6496]', milestone: 'bg-emerald-50 text-emerald-700', delay: 'bg-amber-50 text-amber-700', approval: 'bg-violet-50 text-violet-700', payment: 'bg-orange-50 text-orange-700', general: 'bg-gray-100 text-gray-600' };

export default function PortalPhotos() {
  const { api, user } = usePortal();
  const [updates, setUpdates] = useState([]);
  const [photos, setPhotos] = useState([]);
  const pid = user?.projects?.[0];

  useEffect(() => {
    if (!pid) return;
    api(`/projects/${pid}/updates`).then(r => setUpdates(r.data)).catch(() => {});
    api(`/projects/${pid}/photos`).then(r => setPhotos(r.data)).catch(() => {});
  }, [api, pid]);

  return (
    <div className="space-y-6" data-testid="portal-photos">
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Photos & Updates</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">{updates.length} updates | {photos.length} photos</p>
      </div>

      {/* Photo Gallery */}
      {photos.length > 0 && (
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#1e3a50] mb-3">Photo Gallery</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map(p => (
                <img key={p.id}
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/photos/${p.id}`}
                  alt={p.stage_name} className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/photos/${p.id}`, '_blank')} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Updates Feed */}
      <div className="space-y-3">
        {updates.map(u => (
          <Card key={u.id} className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${u.pinned ? 'ring-1 ring-[#4a9ac5]/30' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-[#1e3a50]">{u.title}</p>
                  <p className="text-[10px] font-medium text-[#8faab8]">{new Date(u.created_at).toLocaleDateString()}</p>
                </div>
                <Badge className={`text-[9px] font-bold capitalize ${CAT_COLORS[u.category] || CAT_COLORS.general}`}>{u.category}</Badge>
              </div>
              {u.summary && <p className="text-xs font-medium text-[#4a6a7e]">{u.summary}</p>}
              {u.body && <p className="text-xs text-[#6b8a9e] mt-1">{u.body}</p>}
            </CardContent>
          </Card>
        ))}
        {updates.length === 0 && photos.length === 0 && (
          <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center">
            <Camera className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm font-medium text-[#6b8a9e]">No updates or photos yet. They'll appear here as your project progresses.</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
